import {
  MANAGED_LEAD_AUTH_UNAVAILABLE_MESSAGE,
  MANAGED_LEAD_OWNER_MISSING_MESSAGE,
  MANAGED_LEAD_SESSION_EXPIRED_MESSAGE,
  readManagedLeadRejectedFields,
} from '../../managedLead/managedLeadFailures';
import {
  classifyTassoQuintoError,
  getTassoQuintoErrorCode,
} from '../../../services/api/tasso-quinto';

/**
 * What a failed review decision means for the queue. Every flag drives one
 * decision, so the panel never has to re-read the envelope code to know what to
 * do next.
 */
export interface ManagedDropReviewFailure {
  message: string;
  /**
   * A decision the server refused because the request had already moved on is
   * not a breakage: the queue was stale and is being re-read.
   */
  severity: 'error' | 'warning';
  /** Pressing Riprova on the same request can still succeed. */
  retryable: boolean;
  /** The queue on screen no longer describes the request and must be re-read. */
  refetchesQueue: boolean;
  /** The session, not the decision, failed: send the user to the login page. */
  sessionExpired: boolean;
  motivationFieldMessage: string | null;
}

/**
 * A HubSpot failure leaves the request pending: saying anything else would
 * claim the Deal is in Drop while it is not.
 */
const RETRYABLE_MESSAGE =
  'Non è stato possibile completare la decisione. La richiesta resta in attesa di revisione e puoi riprovare.';

const FIELD_LABELS: Record<string, string> = { motivation: 'Motivazione' };

const baseFailure: ManagedDropReviewFailure = {
  message: RETRYABLE_MESSAGE,
  severity: 'error',
  retryable: true,
  refetchesQueue: false,
  sessionExpired: false,
  motivationFieldMessage: null,
};

const invalidRequestFailure = (error: unknown): ManagedDropReviewFailure => {
  const { names, labels } = readManagedLeadRejectedFields(error, FIELD_LABELS);

  return {
    ...baseFailure,
    message: labels.length
      ? `La decisione non è valida: controlla ${labels.join(', ')}.`
      : 'La decisione non è valida: controlla i dati inseriti.',
    retryable: false,
    motivationFieldMessage: names.includes('motivation')
      ? 'La motivazione non rispetta i limiti previsti.'
      : null,
  };
};

/**
 * The answer for a response this module has no `code` case for, decided by the
 * read path's classifier so that every managed lead page reads an uncoded or
 * newly minted response the same way. A decision that cannot be retried must
 * not be offered a Riprova that would only be refused again.
 */
const classifiedFailure = (error: unknown): ManagedDropReviewFailure => {
  switch (classifyTassoQuintoError(error)) {
    case 'session_expired':
      return {
        ...baseFailure,
        message: MANAGED_LEAD_SESSION_EXPIRED_MESSAGE,
        severity: 'warning',
        retryable: false,
        sessionExpired: true,
      };
    case 'auth_unavailable':
      return { ...baseFailure, message: MANAGED_LEAD_AUTH_UNAVAILABLE_MESSAGE };
    case 'unauthorized':
      return {
        ...baseFailure,
        message:
          'Non sei autorizzato a revisionare le richieste di Drop di questo prodotto.',
        retryable: false,
      };
    case 'not_found':
      return {
        ...baseFailure,
        message:
          'Questa richiesta di Drop non esiste più. La coda è stata ricaricata.',
        retryable: false,
        refetchesQueue: true,
      };
    case 'owner_missing':
      return {
        ...baseFailure,
        message: MANAGED_LEAD_OWNER_MISSING_MESSAGE,
        retryable: false,
      };
    default:
      return baseFailure;
  }
};

/**
 * Translates a failed approval or rejection into what the admin sees and what
 * the queue does next, reacting to the stable envelope `code` and never to its
 * message.
 */
export const describeManagedDropReviewFailure = (
  error: unknown
): ManagedDropReviewFailure => {
  if (classifyTassoQuintoError(error) === 'session_expired') {
    return classifiedFailure(error);
  }

  switch (getTassoQuintoErrorCode(error)) {
    case 'authentication_unavailable':
      return { ...baseFailure, message: MANAGED_LEAD_AUTH_UNAVAILABLE_MESSAGE };
    case 'insufficient_scope':
      return {
        ...baseFailure,
        message:
          'Non sei autorizzato a revisionare le richieste di Drop di questo prodotto.',
        retryable: false,
      };
    case 'drop_request_not_pending':
      return {
        ...baseFailure,
        message:
          'Questa richiesta non è più in attesa di revisione: era già stata decisa altrove. La coda è stata ricaricata.',
        severity: 'warning',
        retryable: false,
        refetchesQueue: true,
      };
    case 'drop_request_invalidated':
      return {
        ...baseFailure,
        message:
          'La lead ha già raggiunto una fase terminale: la richiesta è stata chiusa come non più valida e nessuna modifica è stata applicata.',
        severity: 'warning',
        retryable: false,
        refetchesQueue: true,
      };
    case 'drop_request_not_found':
      return {
        ...baseFailure,
        message:
          'Questa richiesta di Drop non esiste più. La coda è stata ricaricata.',
        retryable: false,
        refetchesQueue: true,
      };
    case 'deal_not_found':
      return {
        ...baseFailure,
        message:
          'La lead non è più disponibile su questo prodotto: la richiesta resta in coda e non è stata approvata.',
        retryable: false,
        refetchesQueue: true,
      };
    case 'invalid_request':
      return invalidRequestFailure(error);
    default:
      return classifiedFailure(error);
  }
};

/**
 * What a decision left on screen, reported to the queue rather than kept on the
 * card that made it. An approval or a closed request leaves the filtered queue
 * the moment it is re-read, and the card goes with it: the admin would lose the
 * only account of what happened exactly when it matters most.
 */
export interface ManagedDropReviewOutcome {
  requestId: string;
  message: string;
  severity: 'success' | 'error' | 'warning';
  retryable: boolean;
  sessionExpired: boolean;
  /** Re-sends the decision as it was made. */
  retry: () => void;
}

export const MANAGED_DROP_REVIEW_APPROVED_MESSAGE =
  'Richiesta approvata: la lead è passata in Drop con il motivo indicato.';

export const MANAGED_DROP_REVIEW_REJECTED_MESSAGE =
  'Richiesta rifiutata: la motivazione resta nello storico e la lead può ricevere una nuova richiesta.';
